import React, { FunctionComponent, useEffect, useState } from 'react';
import Modal from 'react-modal';
import { Dimmer, Loader } from 'semantic-ui-react';
import { isValidProdigyVIN } from '@upstart/harmony/lib/car-utils';
import { fmtPrice } from '@upstart/harmony/lib/formatter';
import * as changeLogHelper from '@upstart/harmony/lib/deskingoffer-changelog';
import { DeskingOffer } from '@upstart/harmony/lib/legos/deskingTypes';
import { set } from 'lodash';
import { cloneDeep } from 'lodash/fp';
import { scrollableModalStyles } from 'util/constants';
import { isEmbeddedInCommandCenter } from 'util/commandCenter';
import { Action, ModalType } from 'util/amplitudeEvents';
import CloseIcon from '../../Icons/Close';
import ProtectionPlansList from '../../PencilEditor/ProtectionPlansList';
import useProtectionPlans from '../../PencilEditor/useProtectionPlans';
import DealerService from '../../../services/DealerService';
import './ProtectionPlansModal.scss';
import { isOnePayLease } from '../DeskingOfferCard/desking-offer-utils';

const MODAL_STYLE_WARNING = {
  content: {
    margin: '0 auto',
    maxWidth: '20%',
    position: 'relative',
    padding: 0,
  },
};

interface Props {
  car: any;
  deal: any;
  dealer: any;
  offer: DeskingOffer;
  pencil: any;
  isOpen: boolean;
  closeModal: () => void;
  onChange: (offer: DeskingOffer) => void;
  sendAmplitudeEvent?: (events: ModalType, action: Action) => void;
  disabled?: boolean;
}
const ProtectionPlansModal: FunctionComponent<Props> = props => {
  const {
    car,
    closeModal,
    deal,
    dealer,
    isOpen,
    offer,
    pencil,
    onChange,
    sendAmplitudeEvent,
    disabled = false,
  } = props;
  const [dealOwner, setDealOwner] = useState(dealer);
  const [fetchingDealer, setFetchingDealer] = useState(false);
  const [
    protectionPlanProductPriceOverrides,
    setProtectionPlanProductOverrides,
  ] = useState(offer.data?.protectionPlanProductPriceOverrides || {});

  const carDealerId = car?.dealer_id;
  const offerTerm =
    offer.data != null && 'months' in offer.data
      ? offer.data.months
      : undefined;
  const offerMileage =
    offer.data != null && 'mileage' in offer.data
      ? offer.data.mileage
      : undefined;

  const isOnePay = !!isOnePayLease({
    offer,
    deal,
    dealer,
  });

  const {
    rates,
    bundles,
    fetching,
    productList,
    totalPrice,
    termOptions,
    toggleProductSelect,
    toggleInCapCostFlag,
    products,
    setProducts,
    minMaxDebounceInEffect,
    setMinMaxDebounceInEffect,
    productIdBeingEdited,
    setProductIdBeingEdited,
    getSelectedBundles,
    getSelectedProducts,
    updateProductsRatesAndTerms,
  } = useProtectionPlans({
    car,
    deal,
    dealer: dealOwner,
    pencil,
    selectedBundles: offer.data?.selectedBundles,
    selectedProducts: offer.data?.selectedProducts,
    financeType: offer.data?.financeType,
    protectionPlanProductPriceOverrides,
    activeFinanceTerm: offerTerm,
    selectedMileage: offerMileage,
    isOnePayLease: isOnePay,
  });

  useEffect(() => {
    if (isOpen && offer) {
      // Sync with offer prop
      setProtectionPlanProductOverrides(
        offer.data?.protectionPlanProductPriceOverrides || {},
      );

      if (rates) {
        updateProductsRatesAndTerms();
      }
    }
  }, [isOpen, offer]);

  useEffect(() => {
    if (carDealerId && carDealerId !== dealer?.id) {
      const fetchDealer = async (dealerId: string) => {
        setFetchingDealer(true);
        try {
          setDealOwner(await DealerService.getDealer(dealerId));
        } catch (ignore) {} // eslint-disable-line no-empty
        setFetchingDealer(false);
      };
      fetchDealer(carDealerId);
    }
  }, [carDealerId, dealer.id]);

  const isFetching = fetching || fetchingDealer;

  const onSave = () => {
    const selectedBundles = getSelectedBundles(bundles);
    const selectedProducts = getSelectedProducts(bundles, products);
    const newOffer = cloneDeep(offer);
    let newChangeLog = changeLogHelper.updateBundles(newOffer, selectedBundles);
    set(newOffer, 'changeLog', newChangeLog);
    set(newOffer, ['data', 'selectedBundles'], selectedBundles);

    newChangeLog = changeLogHelper.updateProducts(newOffer, selectedProducts);
    set(newOffer, 'changeLog', newChangeLog);
    set(newOffer, ['data', 'selectedProducts'], selectedProducts);

    newChangeLog = changeLogHelper.updatePriceOverrides(
      newOffer,
      protectionPlanProductPriceOverrides,
      dealOwner.penProducts,
    );
    set(newOffer, 'changeLog', newChangeLog);
    set(
      newOffer,
      ['data', `protectionPlanProductPriceOverrides`],
      protectionPlanProductPriceOverrides,
    );
    onChange(newOffer);
    if (sendAmplitudeEvent) {
      sendAmplitudeEvent(ModalType.PROTECTION_PLANS, Action.FINISH);
    }
    closeModal();
  };

  if (isValidProdigyVIN(car?.id)) {
    return (
      <Modal isOpen={isOpen} style={MODAL_STYLE_WARNING as any}>
        <CloseIcon
          onClick={() => {
            if (sendAmplitudeEvent) {
              sendAmplitudeEvent(ModalType.PROTECTION_PLANS, Action.CANCEL);
            }
            closeModal();
          }}
        />
        <div className="protection-plans-modal-header-warning">
          <div className="protection-plans-modal-title">
            This is a vehicle without a VIN
          </div>
          <div>Please enter protection plans using the ADD ONS section</div>
        </div>
      </Modal>
    );
  }

  return (
    <Modal isOpen={isOpen} style={scrollableModalStyles as any}>
      <CloseIcon
        onClick={() => {
          if (sendAmplitudeEvent) {
            sendAmplitudeEvent(ModalType.PROTECTION_PLANS, Action.CANCEL);
          }
          closeModal();
        }}
      />
      {isFetching && (
        <Dimmer active inverted>
          <Loader inverted content="Loading" />
        </Dimmer>
      )}
      <div className="protection-plans-modal-header">
        <div className="protection-plans-modal-title capitalize-in-chairman">
          Protection plans
        </div>
        {!isFetching && (
          <div className="protection-plans-modal-row">
            <div>Total Product Price {fmtPrice(totalPrice)}</div>
            <button
              className="btn btn-primary"
              onClick={onSave}
              disabled={
                disabled || productList.length === 0 || minMaxDebounceInEffect
              }
            >
              {isEmbeddedInCommandCenter() ? 'Save' : 'Save Plans'}
            </button>
          </div>
        )}
      </div>
      <ProtectionPlansList
        fetching={isFetching}
        productList={productList}
        products={products}
        rates={rates ?? undefined}
        termOptions={termOptions}
        protectionPlanProductPriceOverrides={
          protectionPlanProductPriceOverrides
        }
        minMaxDebounceInEffect={minMaxDebounceInEffect}
        productIdBeingEdited={productIdBeingEdited}
        toggleProductSelect={toggleProductSelect}
        toggleInCapCostFlag={toggleInCapCostFlag}
        setProducts={setProducts}
        setProductIdBeingEdited={setProductIdBeingEdited}
        setProtectionPlanProductOverrides={setProtectionPlanProductOverrides}
        setMinMaxDebounceInEffect={setMinMaxDebounceInEffect}
        disabled={disabled}
        offer={offer}
        isOnePayLease={isOnePay}
      />
    </Modal>
  );
};

export default ProtectionPlansModal;
